// RTK Query slice for exceptions management
// Derives data from setupApi (api/auth-restaurant) - no separate API calls
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "./baseQuery";
import { setupApi } from "./setupApi";

export const exceptionsApi = createApi({
  reducerPath: "exceptionsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["Exceptions"],
  endpoints: (builder) => ({
    /**
     * Get all exceptions
     * This doesn't make a new API call - it reads from setupApi's cache
     */
    getExceptions: builder.query({
      queryFn: async (_arg, { getState }, _extraOptions, baseQuery) => {
        // Get data from setupApi cache instead of making a new API call
        const setupState = setupApi.endpoints.getRestaurantSetup.select()(getState());

        // If setupApi has an error, propagate it
        if (setupState.error) {
          return { error: setupState.error };
        }

        // Extract data from setupApi cache (even if still loading, return what we have)
        const exceptions = setupState.data?.exceptions || [];

        return {
          data: exceptions,
        };
      },
      providesTags: ["Exceptions"],
    }),

    /** Create exception */
    createException: builder.mutation({
      query: (body) => ({
        url: "api/exceptions",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Exceptions"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate setupApi to refetch the centralized data
          dispatch(setupApi.util.invalidateTags(["RestaurantSetup"]));
        } catch {}
      },
    }),

    /** Update exception */
    updateException: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `api/exceptions/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Exceptions"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate setupApi to refetch the centralized data
          dispatch(setupApi.util.invalidateTags(["RestaurantSetup"]));
        } catch {}
      },
    }),

    /** Delete exception */
    deleteException: builder.mutation({
      query: (id) => ({
        url: `api/exceptions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Exceptions"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate setupApi to refetch the centralized data
          dispatch(setupApi.util.invalidateTags(["RestaurantSetup"]));
        } catch {}
      },
    }),
  }),
});

export const {
  useGetExceptionsQuery,
  useCreateExceptionMutation,
  useUpdateExceptionMutation,
  useDeleteExceptionMutation,
} = exceptionsApi;
